import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { icons } from '../../components/ui/Icons';
import { cn } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { Table } from '../../components/ui/Table';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { UserWithDetails, DiscordRole, AuditLog, SettingEntry } from '../../types/auth';
import { formatDate } from '../../lib/date';

const API_BASE = '/api/admin';

interface DashboardStats {
  users: { total: number; active: number; inactive: number };
  roles: { total: number; active: number };
  audit: { last24h: number; last7d: number };
  settings: number;
}

async function requestJson<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const text = await response.text();
  return text ? JSON.parse(text) as T : undefined as T;
}

export function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentAudit, setRecentAudit] = useState<AuditLog[]>([]);
  const [recentUsers, setRecentUsers] = useState<UserWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, rolesRes, auditRes, settingsRes] = await Promise.all([
        requestJson<UserWithDetails[]>(`/users`),
        requestJson<DiscordRole[]>(`/discord-roles`),
        requestJson<AuditLog[]>(`/audit?limit=50`),
        requestJson<SettingEntry[]>(`/settings`),
      ]);

      const users = Array.isArray(usersRes) ? usersRes : [];
      const roles = Array.isArray(rolesRes) ? rolesRes : [];
      const audit = Array.isArray(auditRes) ? auditRes : [];
      const settings = Array.isArray(settingsRes) ? settingsRes : [];

      const now = Date.now();
      const dayMs = 24 * 60 * 60 * 1000;
      const weekMs = 7 * dayMs;

      setStats({
        users: {
          total: users.length,
          active: users.filter(u => u.isActive).length,
          inactive: users.filter(u => !u.isActive).length,
        },
        roles: {
          total: roles.length,
          active: roles.filter(r => r.isActive).length,
        },
        audit: {
          last24h: audit.filter(log => now - new Date(log.createdAt).getTime() < dayMs).length,
          last7d: audit.filter(log => now - new Date(log.createdAt).getTime() < weekMs).length,
        },
        settings: settings.length,
      });

      setRecentAudit(audit.slice(0, 5));
      setRecentUsers(
        [...users]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5)
      );
    } catch (err) {
      setError('Übersicht konnte nicht geladen werden.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const statCards = useMemo(() => [
    {
      label: 'Benutzer',
      value: stats?.users.total ?? '-',
      subValue: stats !== null ? `${stats.users.active} aktiv · ${stats.users.inactive} inaktiv` : null,
      icon: 'Users',
      color: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
      link: '/admin/access/users',
    },
    {
      label: 'Discord-Rollen',
      value: stats?.roles.total ?? '-',
      subValue: stats !== null ? `${stats.roles.active} aktiv` : null,
      icon: 'Shield',
      color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
      link: '/admin/access/roles',
    },
    {
      label: 'Audit-Log (24h)',
      value: stats?.audit.last24h ?? '-',
      subValue: stats !== null ? `${stats.audit.last7d} in 7 Tagen` : null,
      icon: 'History',
      color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      link: '/admin/security/audit',
    },
    {
      label: 'Systemeinstellungen',
      value: stats?.settings ?? '-',
      subValue: null,
      icon: 'Settings',
      color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
      link: '/admin/organization/settings',
    },
  ], [stats]);

  const renderStatIcon = (iconName: string) => {
    const Icon = icons[iconName as keyof typeof icons];
    return Icon ? <Icon className="h-6 w-6" aria-hidden="true" /> : null;
  };

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Übersicht', current: true },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={breadcrumbItems} />
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Übersicht</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Zentrale Kennzahlen und aktuelle Aktivitäten im Überblick.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-neutral-200 dark:border-neutral-700">
              <CardContent className="py-6">
                <SkeletonTable rows={3} columns={1} />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={breadcrumbItems} />
        <Alert variant="error" title="Fehler beim Laden der Übersicht" dismissible onDismiss={() => setError(null)}>
          {error}
          <Button variant="outline" size="sm" className="mt-2" leftIcon="RefreshCw" onClick={fetchOverview}>
            Erneut versuchen
          </Button>
        </Alert>
      </div>
    );
  }

  const auditActionVariant = (action: string) => {
    if (/DENY|REMOVE|DELETE|FAILED|ERROR/i.test(action)) return 'emergency';
    if (/GRANT|CREATE|UPDATE|SYNC|LOGIN/i.test(action)) return 'medical';
    return 'primary';
  };

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Übersicht</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Zentrale Kennzahlen und aktuelle Aktivitäten im Überblick.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.label} className="border-neutral-200 dark:border-neutral-700 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <Link to={stat.link} className="group flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{stat.label}</p>
                  <p className="mt-1 text-3xl font-bold text-neutral-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {stat.value}
                  </p>
                  {stat.subValue && (
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{stat.subValue}</p>
                  )}
                </div>
                <div className={cn('flex-shrink-0 h-12 w-12 items-center justify-center rounded-lg', stat.color)}>
                  {renderStatIcon(stat.icon)}
                </div>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Audit Logs */}
        <Card className="border-neutral-200 dark:border-neutral-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Letzte Audit-Einträge</CardTitle>
              <CardDescription>Die 5 aktuellsten Systemereignisse.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/security/audit">Alle anzeigen <icons.ChevronRight className="h-3.5 w-3.5 ml-1" /></Link>
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {recentAudit.length === 0 ? (
              <EmptyState icon="History" title="Keine Audit-Einträge" description="Noch keine Systemereignisse vorhanden." className="py-8" />
            ) : (
              <Table
                columns={[
                  {
                    key: 'createdAt',
                    header: 'Zeit',
                    className: 'w-36 font-mono text-xs',
                    render: (log: AuditLog) => <span className="whitespace-nowrap text-neutral-700 dark:text-neutral-300">{formatDate(log.createdAt, true)}</span>,
                  },
                  {
                    key: 'action',
                    header: 'Aktion',
                    render: (log: AuditLog) => (
                      <Badge variant={auditActionVariant(log.action)} size="sm">{log.action}</Badge>
                    ),
                  },
                  {
                    key: 'user',
                    header: 'Benutzer',
                    className: 'min-w-[140px]',
                    render: (log: AuditLog) => (
                      <div>
                        <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">{log.userName || '-'}</p>
                        <p className="truncate text-xs text-neutral-500 dark:text-neutral-400 font-mono">{log.userId}</p>
                      </div>
                    ),
                  },
                  {
                    key: 'resource',
                    header: 'Bereich',
                    render: (log: AuditLog) => <span className="text-sm text-neutral-700 dark:text-neutral-300">{log.resource}</span>,
                  },
                ]}
                data={recentAudit}
                keyExtractor={(log) => log.id}
                hoverable={false}
                striped={true}
                compact={true}
              />
            )}
          </CardContent>
        </Card>

        {/* Recent Users */}
        <Card className="border-neutral-200 dark:border-neutral-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Neueste Benutzer</CardTitle>
              <CardDescription>Die 5 zuletzt angelegten Benutzerkonten.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/access/users">Alle anzeigen <icons.ChevronRight className="h-3.5 w-3.5 ml-1" /></Link>
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {recentUsers.length === 0 ? (
              <EmptyState icon="Users" title="Keine Benutzer" description="Noch keine Benutzerkonten vorhanden." className="py-8" />
            ) : (
              <Table
                columns={[
                  {
                    key: 'user',
                    header: 'Benutzer',
                    className: 'min-w-[200px]',
                    render: (user: UserWithDetails) => (
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 flex-shrink-0 rounded-full bg-primary-100 text-primary-700 items-center justify-center text-sm font-medium dark:bg-primary-900/30 dark:text-primary-300">
                          {(user.discordDisplayName || user.discordUsername).charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-neutral-900 dark:text-white">{user.discordDisplayName || user.discordUsername}</p>
                          <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">@{user.discordUsername}</p>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'email',
                    header: 'E-Mail',
                    render: (user: UserWithDetails) => <span className="truncate text-sm text-neutral-700 dark:text-neutral-300">{user.email || '-'}</span>,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    className: 'w-24',
                    render: (user: UserWithDetails) => (
                      <Badge variant={user.isActive ? 'medical' : 'gray'} size="sm">{user.isActive ? 'Aktiv' : 'Inaktiv'}</Badge>
                    ),
                  },
                  {
                    key: 'createdAt',
                    header: 'Erstellt',
                    className: 'w-32 font-mono text-xs',
                    render: (user: UserWithDetails) => <span className="whitespace-nowrap text-neutral-700 dark:text-neutral-300">{formatDate(user.createdAt)}</span>,
                  },
                ]}
                data={recentUsers}
                keyExtractor={(user) => user.id}
                hoverable={false}
                striped={true}
                compact={true}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardHeader>
          <CardTitle>Schnellzugriff</CardTitle>
          <CardDescription>Häufig genutzte Verwaltungsaktionen.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" leftIcon="UserPlus" asChild>
              <Link to="/admin/access/users">Benutzer anlegen</Link>
            </Button>
            <Button variant="outline" leftIcon="Shield" asChild>
              <Link to="/admin/access/roles">Discord-Rollen synchronisieren</Link>
            </Button>
            <Button variant="outline" leftIcon="Key" asChild>
              <Link to="/admin/access/permissions">Berechtigungen verwalten</Link>
            </Button>
            <Button variant="outline" leftIcon="History" asChild>
              <Link to="/admin/security/audit">Audit-Log prüfen</Link>
            </Button>
            <Button variant="outline" leftIcon="Settings" asChild>
              <Link to="/admin/organization/settings">Systemeinstellungen</Link>
            </Button>
            <Button variant="outline" leftIcon="Activity" asChild>
              <Link to="/admin/system/status">Systemstatus prüfen</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}